import { Tag, Tooltip } from 'antd';
import { FC } from 'react';

import { formatNumber } from '@constants';

type DifferenceTagProps = {
  difference: number;
  currencyCode: string;
  /** What this amount is the difference *of*, when the column header cannot carry it on its own. */
  scope?: string;
};

/**
 * An unexplained amount, with its direction on the face of it.
 *
 * The sign is the whole reason a chain can total nothing while every interval in it failed: `+12.00`,
 * `+16.99`, `+12.14` and then `−41.13` cancel exactly, which is a bank that posted its balance four
 * days late — not four unrelated problems, and not one problem either. Rendered with the same weight
 * in one colour, that pattern is invisible; the reader sees four red figures and reads four faults.
 *
 * So direction is carried twice, redundantly and on purpose: an explicit `+` in the number and a
 * background that changes with it. Either alone survives a screenshot, a colour-blind reader or a
 * glance down the column badly.
 */
const DifferenceTag: FC<DifferenceTagProps> = ({
  difference,
  currencyCode,
  scope,
}) => {
  const direction =
    difference < 0
      ? 'The bank took out more than the transactions we hold account for.'
      : 'The bank paid in more than the transactions we hold account for.';

  return (
    <Tooltip title={scope ? `${scope} ${direction}` : direction}>
      <Tag color={difference < 0 ? 'red' : 'blue'}>
        {formatNumber(difference, {
          style: 'currency',
          currency: currencyCode,
          // `exceptZero` rather than `always`: a zero here is an interval that adds up, and a `+0.00`
          // would read as a discrepancy too small to see rather than as no discrepancy.
          signDisplay: 'exceptZero',
        })}
      </Tag>
    </Tooltip>
  );
};

export default DifferenceTag;
